import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ['cardElement', 'cardErrors', 'form', 'submit', 'acceptTerms', 'addressStreet1', 'addressCity', 'addressPostalCode', 'addressCountry']
  static values = {
    key: String,
    customerName: String,
    customerEmail: String,
    useSource: Boolean
  }

  async connect() {
    if (!this.keyValue) {
      console.error("Missing Stripe key")
      return
    }

    await this.loadStripeLibraries()

    this.stripe = Stripe(this.keyValue)
    this.elements = this.stripe.elements()

    this.isSubmitting = false

    this.configureCard()

    this.originalButtonText = this.submitTarget.textContent

    // Prevent Rails UJS from interfering with our button state management
    if (this.hasSubmitTarget) {
      this.submitTarget.removeAttribute('data-disable-with')
    }

    if (this.hasFormTarget) {
      this.formTarget.addEventListener('turbo:submit-end', () => {
        this.resetButton()
      })
    }
  }

  acceptTermsTargetConnected() {
    this.updateSubmitButtonState()
  }

  updateSubmitButtonState() {
    if (this.hasAcceptTermsTarget && this.hasSubmitTarget) {
      const isChecked = this.acceptTermsTarget.checked

      if (isChecked) {
        this.submitTarget.removeAttribute('disabled')
        this.submitTarget.disabled = false
        this.submitTarget.classList.remove('tw-opacity-50', 'tw-cursor-not-allowed')
        this.submitTarget.classList.add('hover:tw-bg-teal-800')
      } else {
        this.submitTarget.setAttribute('disabled', 'disabled')
        this.submitTarget.disabled = true
        this.submitTarget.classList.add('tw-opacity-50', 'tw-cursor-not-allowed')
        this.submitTarget.classList.remove('hover:tw-bg-teal-800')
      }
    }
  }

  async saveCard(event) {
    event.preventDefault()

    if (this.isSubmitting) {
      return
    }

    this.startProcessing()
    this.setProcessingTimeout()

    try {
      const result = await this.createPaymentMethod()
      this.handlePaymentResult(result)
    } catch (error) {
      this.handleError("An error occurred. Please try again.", error)
    }
  }

  startProcessing() {
    this.isSubmitting = true
    this.submitTarget.disabled = true
    this.submitTarget.textContent = "Processing..."
  }

  setProcessingTimeout() {
    this.timeoutId = setTimeout(() => {
      this.cardErrorsTarget.textContent = "This is taking too long. Please try again."
      this.resetButton()
    }, 10000)
  }

  async createPaymentMethod() {
    if (this.useSourceValue) {
      const ownerInfo = this.buildOwnerInfo()
      return await this.stripe.createSource(this.card, ownerInfo)
    } else {
      return await this.stripe.createToken(this.card)
    }
  }

  handlePaymentResult(result) {
    if (result.error) {
      const errorMessage = this.useSourceValue
        ? this.manageStripeErrors(result.error)
        : result.error.message
      this.handleError(errorMessage)
    } else {
      this.handleSuccessfulResult(result)
    }
  }

  handleSuccessfulResult(result) {
    if (this.useSourceValue) {
      this.submitFormWithSource(result.source)
    } else {
      this.submitFormWithToken(result.token)
    }
  }

  handleError(message, error = null) {
    this.cardErrorsTarget.textContent = message
    this.resetButton()
    if (error) {
      console.error("Stripe error:", error)
    }
  }

  buildOwnerInfo() {
    const ownerInfo = {
      owner: {
        name: this.customerNameValue,
        email: this.customerEmailValue
      }
    }

    if (this.hasAddressStreet1Target && this.hasAddressCityTarget &&
        this.hasAddressPostalCodeTarget && this.hasAddressCountryTarget) {
      ownerInfo.owner.address = {
        line1: this.addressStreet1Target.value,
        city: this.addressCityTarget.value,
        postal_code: this.addressPostalCodeTarget.value,
        country: this.addressCountryTarget.value
      }
    }

    return ownerInfo
  }

  manageStripeErrors(error) {
    if (error.code === 'email_invalid') {
      // Customer must have contact info for Stripe to process payment methods
      return 'Please add at least one contact to this customer to be able to add or change a Credit Card'
    } else {
      return error.message
    }
  }

  configureCard() {
    if (!this.elements) {
      console.error("Stripe elements not initialized")
      return
    }

    this.card = this.elements.create('card', {
      style: this.cardStyle
    })

    try {
      this.card.mount(this.cardElementTarget)
    } catch (error) {
      console.error('Error mounting card element:', error)
    }

    this.card.on('change', (event) => {
      if (event.error) {
        this.cardErrorsTarget.textContent = this.useSourceValue ? this.manageStripeErrors(event.error) : event.error.message
      } else {
        this.cardErrorsTarget.textContent = ''
      }
    })
  }

  submitFormWithToken(token) {
    clearTimeout(this.timeoutId)

    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'stripeToken'
    hiddenInput.value = token.id
    this.formTarget.appendChild(hiddenInput)

    this.formTarget.requestSubmit()
  }

  submitFormWithSource(source) {
    clearTimeout(this.timeoutId)

    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'source[source_id]'
    hiddenInput.value = source.id
    this.formTarget.appendChild(hiddenInput)

    this.formTarget.submit()
  }

  get cardStyle() {
    return {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  }

  async loadStripeLibraries() {
    const scripts = []

    // Stripe v2 required for legacy tokenization compatibility
    if (!document.querySelector('script[src="https://js.stripe.com/v2/"]')) {
      scripts.push(this.loadScript("https://js.stripe.com/v2/"))
    }

    if (!document.querySelector('script[src="https://js.stripe.com/v3/"]')) {
      scripts.push(this.loadScript("https://js.stripe.com/v3/"))
    }

    return Promise.all(scripts)
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  resetButton() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    this.isSubmitting = false

    this.submitTarget.removeAttribute('disabled')
    this.submitTarget.disabled = false

    this.submitTarget.textContent = this.originalButtonText || 'Securely Save Payment Method'

    // Remove any Rails UJS classes that might have been added
    this.submitTarget.classList.remove('disabled')

    // Re-apply checkbox state to button
    this.updateSubmitButtonState()
  }
}
